import React, { useState, useEffect, useCallback } from 'react';
import { AlertOctagon, Check, X, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

export const CriticalReviewQueue = () => {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiClient.request('/api/admin/safety/critical-reviews?limit=50'));
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, verdict) => {
    setBusy(id);
    try {
      await apiClient.request(`/api/admin/safety/critical-reviews/${id}/review`, {
        method: 'POST', body: JSON.stringify({ verdict, notes: notes[id] || '' }),
      });
      await load();
    } catch (e) { console.error(e); }
    setBusy(null);
  };

  if (!data) return null;
  const { events, stats } = data;
  const visible = showReviewed ? events : events.filter(e => e.review_status === 'pending');

  return (
    <div className="bg-white p-5 rounded-lg mb-6" style={{ border: '2px solid #DC2626' }} data-testid="critical-review-queue">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold flex items-center gap-2" style={{ color: '#DC2626' }}>
          <AlertOctagon size={18} /> CRITICAL Event Review Ledger
        </h3>
        <button onClick={() => setShowReviewed(s => !s)} className="text-xs underline" style={{ color: '#44403A' }} data-testid="crq-toggle-reviewed">
          {showReviewed ? 'Show pending only' : 'Show reviewed too'}
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: '#44403A' }}>
        Founder policy: a human reviews every CRITICAL (&ge;0.95) event so the lexicon can be tuned without weakening the rule.
        Matched terms shown; raw messages are never stored.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="crq-stats">
        <div><p className="text-xs" style={{ color: '#44403A' }}>Total CRITICAL</p><p className="text-xl font-bold" data-testid="crq-total">{stats.total_critical}</p></div>
        <div><p className="text-xs" style={{ color: '#44403A' }}>Pending review</p><p className="text-xl font-bold" style={{ color: '#D97706' }} data-testid="crq-pending">{stats.pending}</p></div>
        <div><p className="text-xs" style={{ color: '#44403A' }}>True positives</p><p className="text-xl font-bold" style={{ color: '#DC2626' }} data-testid="crq-tp">{stats.true_positives}</p></div>
        <div><p className="text-xs" style={{ color: '#44403A' }}>False positives</p><p className="text-xl font-bold" style={{ color: '#16A34A' }} data-testid="crq-fp">{stats.false_positives}</p></div>
        <div><p className="text-xs" style={{ color: '#44403A' }}>FP rate</p><p className="text-xl font-bold" data-testid="crq-fp-rate">{stats.false_positive_rate === null ? '—' : `${Math.round(stats.false_positive_rate * 100)}%`}</p></div>
      </div>
      {visible.length === 0 ? (
        <p className="text-sm py-3 text-center" style={{ color: '#9A9186' }} data-testid="crq-empty">No {showReviewed ? '' : 'pending '}CRITICAL events. 🎉</p>
      ) : (
        <div className="space-y-3">
          {visible.map(ev => (
            <div key={ev.event_id} className="rounded-lg p-3" style={{ border: '1px solid #E0E0E0', background: ev.review_status === 'pending' ? '#FEF2F2' : '#FAFAF9' }} data-testid={`crq-event-${ev.event_id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
                <span className="font-bold" style={{ color: '#1A1209' }}>{ev.user_name}</span>
                <span style={{ color: '#9A9186' }}>{ev.user_email}</span>
                {ev.is_test_account && <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-bold text-[10px]">TEST</span>}
                <span style={{ color: '#9A9186' }}>{(ev.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                <span className="px-1.5 py-0.5 rounded font-bold text-[10px]" style={{ background: '#DC2626', color: '#fff' }}>score {ev.crisis_score}</span>
                <span style={{ color: '#44403A' }}>nudge L{ev.nudge_level} · {ev.trend}</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(ev.matched_terms || []).map((t, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full text-[10px]" style={{ background: '#FEE2E2', color: '#B91C1C' }}>{t}</span>
                ))}
                {(!ev.matched_terms || ev.matched_terms.length === 0) && <span className="text-[10px]" style={{ color: '#9A9186' }}>no term snapshot (pre-ledger event)</span>}
              </div>
              {ev.review_status === 'pending' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={notes[ev.event_id] || ''}
                    onChange={e => setNotes(n => ({ ...n, [ev.event_id]: e.target.value }))}
                    placeholder="Review notes (e.g. song lyric quote)"
                    className="flex-1 min-w-[180px] text-xs px-2 py-1.5 rounded border"
                    style={{ borderColor: '#E0E0E0' }}
                    data-testid={`crq-notes-${ev.event_id}`}
                  />
                  <button onClick={() => review(ev.event_id, 'true_positive')} disabled={busy === ev.event_id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold text-white" style={{ background: '#DC2626' }}
                    data-testid={`crq-tp-btn-${ev.event_id}`}>
                    {busy === ev.event_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Genuine risk
                  </button>
                  <button onClick={() => review(ev.event_id, 'false_positive')} disabled={busy === ev.event_id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold text-white" style={{ background: '#16A34A' }}
                    data-testid={`crq-fp-btn-${ev.event_id}`}>
                    <X size={12} /> False positive
                  </button>
                </div>
              ) : (
                <p className="text-xs" style={{ color: '#44403A' }} data-testid={`crq-verdict-${ev.event_id}`}>
                  <span className="font-bold" style={{ color: ev.review_status === 'true_positive' ? '#DC2626' : '#16A34A' }}>
                    {ev.review_status === 'true_positive' ? 'GENUINE RISK' : 'FALSE POSITIVE'}
                  </span>
                  {' '}by {ev.reviewed_by} · {(ev.reviewed_at || '').slice(0, 16).replace('T', ' ')}
                  {ev.review_notes && <> — “{ev.review_notes}”</>}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CriticalReviewQueue;
